import api from '@/store/api';
import { athena, boro } from '@/store/api/instances';

interface GetCaseNoteAttachmentRequest {
  case_id: string;
  note_id: string;
  attachment_id: string;
}

interface GetCaseNoteAttachmentResponse {
  id: string;
  note_id: string;
  file_name: string;
  file_url: string;
  content_type: string;
  uploaded_by: string;
  uploaded_at: string;
}

interface CreateCaseNoteRequest {
  case_id: string;
  note_text: string;
  author: string;
}

interface CaseNoteAttachment {
  id: string;
  note_id: string;
  file_name: string;
  file_url: string;
  content_type: string;
  uploaded_by: string;
  uploaded_at: string;
}

interface CreateCaseNoteResponse {
  id: string;
  case_id: string;
  body: string;
  author: string;
  type: string;
  created_by: string;
  created_at: string;
  last_updated_by: string;
  last_updated_at: string;
  attachments: CaseNoteAttachment[];
}

interface UploadCaseNoteAttachmentRequest {
  case_id: string;
  case_note_id: string;
  files: File[];
}

interface UploadCaseNoteAttachmentResponse {
  id: string;
  case_note_id: string;
  file_name: string;
  file_url: string;
  uploaded_by: string;
  uploaded_at: string;
}

/**
 * RTK Query API for Case Notes.
 */
export const caseNotesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCaseNoteAttachment: build.query<GetCaseNoteAttachmentResponse, GetCaseNoteAttachmentRequest>(
      {
        query: ({ case_id, note_id, attachment_id }) =>
          athena({
            url: `/v2/Operations/cases/${case_id}/notes/${note_id}/attachments/${attachment_id}`,
            method: 'GET',
          }),
      },
    ),
    createCaseNote: build.mutation<CreateCaseNoteResponse, CreateCaseNoteRequest>({
      query: ({ case_id, ...data }) =>
        boro({
          url: `/v2/Operations/cases/${case_id}/notes`,
          method: 'POST',
          data,
        }),
      invalidatesTags: (_, __, { case_id }) => [
        { type: 'Case', id: case_id },
        { type: 'CaseNotes', id: case_id },
      ],
    }),
    uploadCaseNoteAttachment: build.mutation<
      UploadCaseNoteAttachmentResponse,
      UploadCaseNoteAttachmentRequest
    >({
      query: ({ case_id, case_note_id, files }) => {
        const formData = new FormData();
        // Backend expects 'files' (plural) and supports multiple files
        files.forEach((file) => {
          formData.append('files', file);
        });

        return boro({
          url: `/v2/Operations/cases/${case_id}/notes/${case_note_id}/attachments`,
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      },
      invalidatesTags: (_, __, { case_id }) => [
        { type: 'Case', id: case_id },
        { type: 'CaseNotes', id: case_id },
      ],
    }),
  }),
});

export const {
  useGetCaseNoteAttachmentQuery,
  useLazyGetCaseNoteAttachmentQuery,
  useCreateCaseNoteMutation,
  useUploadCaseNoteAttachmentMutation,
} = caseNotesApi;

export const {
  endpoints: { getCaseNoteAttachment, createCaseNote, uploadCaseNoteAttachment },
} = caseNotesApi;
